import { apiClient } from '@shared/api';
import type { ApiResponse, PagedResult } from '@shared/types';
import type { NetSuiteEmployeeDto } from '@features/users/types';
import type { PicklistDto, PicklistPickerDto, PicklistSearchResultDto, CreatePicklistRequest, ItemPendingQuantityDto, PostPicklistRequest, AdjustmentReasonDto, AddPicklistItemsRequest, RemovePicklistItemsRequest, AdjustPicklistItemRequest, PostingBreakdownRow, PickHistoryDto, PicklistCountsDto, PicklistMrsSummaryDto, ReplaceSerialRequest, SerialCommitmentDto, SerialCommitmentsRequest, SwapSerialDto, BinHoldDto, BinHoldsRequest } from '../types';

class PicklistService {
  private readonly baseUrl = '/picklist';

  async create(request: CreatePicklistRequest): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      this.baseUrl,
      request
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to create picklist');
  }

  async getAll(): Promise<PicklistDto[]> {
    const { data } = await apiClient.get<ApiResponse<PicklistDto[]>>(
      this.baseUrl
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch picklists');
  }

  async getOpen(viewAll = false): Promise<PicklistDto[]> {
    const { data } = await apiClient.get<ApiResponse<PicklistDto[]>>(
      `${this.baseUrl}/open`,
      { params: { viewAll } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch open picklists');
  }

  async getClosed(): Promise<PicklistDto[]> {
    const { data } = await apiClient.get<ApiResponse<PicklistDto[]>>(
      `${this.baseUrl}/closed`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch closed picklists');
  }

  async getClosedPaged(params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDesc?: boolean;
    viewAll?: boolean;
    /**
     * PW-23 — server-side filter on PL#, MRS# and item code, so the Closed tab can narrow a SERVER-PAGED
     * table. Filtering it client-side is impossible by construction: the client holds one page.
     */
    search?: string;
  }): Promise<PagedResult<PicklistDto>> {
    const { data } = await apiClient.get<ApiResponse<PagedResult<PicklistDto>>>(
      `${this.baseUrl}/closed/paged`,
      { params }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch closed picklists');
  }

  /**
   * PW-23 — the two tab counts in one cheap call. Replaces PW-12's stand-in, which fetched
   * `closed/paged?page=1&pageSize=1` purely to read `totalCount` off it and then threw the row away —
   * a full object graph (items, bins, MIS references, picking histories) dragged across the wire to
   * learn one integer.
   */
  async getCounts(viewAll = false): Promise<PicklistCountsDto> {
    const { data } = await apiClient.get<ApiResponse<PicklistCountsDto>>(
      `${this.baseUrl}/counts`,
      { params: { viewAll } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch picklist counts');
  }

  async searchClosed(query: string, viewAll = false): Promise<PicklistSearchResultDto[]> {
    const { data } = await apiClient.get<ApiResponse<PicklistSearchResultDto[]>>(
      `${this.baseUrl}/closed/search`,
      { params: { query, viewAll } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to search closed picklists');
  }

  async getPending(): Promise<PicklistDto[]> {
    const { data } = await apiClient.get<ApiResponse<PicklistDto[]>>(
      `${this.baseUrl}/pending`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch pending picklists');
  }

  async getById(id: number, viewAll = false): Promise<PicklistDto> {
    const { data } = await apiClient.get<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${id}`,
      { params: { viewAll } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Picklist not found');
  }

  /**
   * 86d3n1xum — per-(bin, serial/lot) breakdown of what this picklist posted to MIS.
   * Lazy-fetched once when a posted Progress cell is first opened; grouped by MIS on the client.
   */
  async getPostingBreakdown(id: number): Promise<PostingBreakdownRow[]> {
    const { data } = await apiClient.get<ApiResponse<PostingBreakdownRow[]>>(
      `${this.baseUrl}/${id}/posting-breakdown`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to load posting breakdown');
  }

  /**
   * The detail page's Pick history (owner 2026-10-08): every pick-ledger entry, grouped by Submit and Adjust.
   * No `viewAll`: visibility follows the caller's permission, as `posting-breakdown` does.
   */
  async getPickHistory(id: number): Promise<PickHistoryDto> {
    const { data } = await apiClient.get<ApiResponse<PickHistoryDto>>(`${this.baseUrl}/${id}/pick-history`);

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to load the pick history');
  }

  /**
   * PT-7 B4 — every MRS on the picklist, with its fulfilment figures, for the shared hover card. The by-id read
   * already carries these (`mrsSummaries`); the landing's list reads do not, so its cards load them lazily here.
   * No `viewAll`: visibility follows the caller's permission, as `posting-breakdown` does.
   */
  async getMrsSummaries(id: number): Promise<PicklistMrsSummaryDto[]> {
    const { data } = await apiClient.get<ApiResponse<PicklistMrsSummaryDto[]>>(
      `${this.baseUrl}/${id}/mrs-summaries`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to load MRS figures');
  }
  /**
   * Fetch categorized pending quantities for items across all unposted picklists.
   * Returns pending picking and pending posting quantities grouped by NetSuite item ID.
   */
  async getPendingQuantities(netsuiteItemIds: number[]): Promise<ItemPendingQuantityDto[]> {
    if (netsuiteItemIds.length === 0) {
      return [];
    }

    const { data } = await apiClient.post<ApiResponse<ItemPendingQuantityDto[]>>(
      `${this.baseUrl}/pending-quantities`,
      { netsuiteItemIds }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch pending quantities');
  }
  /**
   * Claim the picklist for async MIS posting (86d3kwt7h). Returns 202 with the new posting-batch id; the
   * PicklistPostingWorker creates the NetSuite Inventory Adjustment off the request thread. The caller
   * polls getById() until the batch settles (Posted / PostingFailed). Fast — no 5-minute timeout.
   */
  async claimPost(request: PostPicklistRequest): Promise<number> {
    const { data } = await apiClient.post<ApiResponse<{ postingBatchId: number }>>(
      `${this.baseUrl}/${request.picklistId}/post`,
      {
        items: request.items,
        reasonForAdjustmentId: request.reasonForAdjustmentId,
        itemMemos: request.itemMemos,
        itemReadings: request.itemReadings,
        requestedByEmployeeId: request.requestedByEmployeeId,
        // PW-25 — the name rides with the id so the posted slip stays readable for ever.
        requestedByName: request.requestedByName,
        // E7 item 3 — see PostPicklistRequest.clientKey. A replay answers 200 (instead of 202) with the
        // batch the first claim created; both shapes carry postingBatchId, so nothing here branches on it.
        clientKey: request.clientKey,
      }
    );

    if (data.success && data.data) {
      return data.data.postingBatchId;
    }

    throw new Error(data.message || 'Failed to start MIS posting');
  }

  /** Retry a failed MIS post attempt (86d3kwt7h). Re-arms the specific batch (keeps its UUID). Returns 202. */
  async retryMisPost(postingBatchId: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(
      `${this.baseUrl}/posting-batches/${postingBatchId}/retry`,
      {}
    );
    if (!data.success) {
      throw new Error(data.message || 'Failed to retry MIS posting');
    }
  }

  /** 86d3mnuxe funnel — cancel a NotReached-failed batch posting (Failed → Cancelled). The BE
   *  refuses (400) when the post may have reached NetSuite (Retry reconciles instead). */
  async cancelMisPost(postingBatchId: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(
      `${this.baseUrl}/posting-batches/${postingBatchId}/cancel`,
      {}
    );
    if (!data.success) {
      throw new Error(data.message || 'Failed to cancel MIS posting');
    }
  }

  async searchNetSuiteEmployees(search: string): Promise<NetSuiteEmployeeDto[]> {
    const { data } = await apiClient.get<ApiResponse<NetSuiteEmployeeDto[]>>(
      `${this.baseUrl}/netsuite-employees`,
      { params: { search } }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to search NetSuite employees');
  }

  /**
   * Add items to an existing picklist.
   */
  async addItems(picklistId: number, request: AddPicklistItemsRequest): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/add-items`,
      request
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to add items to picklist');
  }

  /**
   * Remove items from an existing picklist.
   */
  async removeItems(picklistId: number, request: RemovePicklistItemsRequest): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/remove-items`,
      request
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to remove items from picklist');
  }

  /**
   * PT-6 — Adjust v2 on one item group (`picklistItemId`: its anchor or any line id). Returns the by-id shape.
   * Throws the AxiosError untouched, so the dialog can branch on the status (409 = the figures changed).
   */
  async adjustItem(
    picklistId: number,
    picklistItemId: number,
    request: AdjustPicklistItemRequest,
  ): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/items/${picklistItemId}/adjust`,
      request,
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to adjust item');
  }

  /**
   * PT-6 E1 — the Swap serial list for one slot: every on-hand serial of the item except the slot's own, with
   * PT-2's statuses. One NetSuite read per call: fired when the dialog opens, never per row.
   */
  async getReplacementSerials(picklistId: number, itemId: number, picklistItemBinId: number): Promise<SwapSerialDto[]> {
    const { data } = await apiClient.get<ApiResponse<SwapSerialDto[]>>(
      `${this.baseUrl}/${picklistId}/items/${itemId}/replacement-serials`,
      { params: { picklistItemBinId, viewAll: true } },
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to load serials');
  }

  /**
   * Which of these on-hand serials another open picklist holds (owner, 2026-10-05). The Allocate Bins dialog's
   * read: one database round trip, no NetSuite call. An empty list is a real answer — nothing is held.
   */
  async getSerialCommitments(request: SerialCommitmentsRequest): Promise<SerialCommitmentDto[]> {
    const { data } = await apiClient.post<ApiResponse<SerialCommitmentDto[]>>(
      `${this.baseUrl}/serial-commitments`,
      request,
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Couldn’t check which serials are on other picklists.');
  }

  /**
   * What other open picklists hold of each bin of these items (14ykpqtv7nd) — the figure creation checks a hand-picked
   * lot or untracked claim against. One database round trip, no NetSuite call. An empty list means nothing is held.
   */
  async getBinHolds(request: BinHoldsRequest): Promise<BinHoldDto[]> {
    const { data } = await apiClient.post<ApiResponse<BinHoldDto[]>>(`${this.baseUrl}/bin-holds`, request);

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Couldn’t check which bins are held by other picklists.');
  }

  /** PT-6 E3 — the bin card's Swap serial. Returns the by-id shape; 409 when the slot's serial is stale. */
  async replaceSerial(picklistId: number, itemId: number, request: ReplaceSerialRequest): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/items/${itemId}/replace-serial`,
      request,
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to swap the serial');
  }

  /**
   * Close a picklist (finalize — blocks further picking, posting, and editing).
   * A reason is required when the picklist has unpicked quantities (partial fulfillment).
   */
  async close(picklistId: number, reason?: string): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/close`,
      { reason: reason || null }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to close picklist');
  }

  /**
   * Cancel a picklist (void — only allowed when no picking has started).
   */
  async cancel(picklistId: number, reason: string): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/cancel`,
      { reason }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to cancel picklist');
  }

  /**
   * Get users eligible to be assigned as pickers (have fulfill_picklist permission).
   */
  async getEligiblePickers(): Promise<PicklistPickerDto[]> {
    const { data } = await apiClient.get<ApiResponse<PicklistPickerDto[]>>(
      `${this.baseUrl}/eligible-pickers`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch eligible pickers');
  }

  /**
   * Add a picker to a picklist.
   */
  async addPicker(picklistId: number, userId: number): Promise<PicklistDto> {
    const { data } = await apiClient.post<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/pickers`,
      { userId }
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to add picker');
  }

  /**
   * Remove a picker from a picklist.
   */
  async removePicker(picklistId: number, userId: number): Promise<PicklistDto> {
    const { data } = await apiClient.delete<ApiResponse<PicklistDto>>(
      `${this.baseUrl}/${picklistId}/pickers/${userId}`
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to remove picker');
  }

  /**
   * Fetch adjustment reasons from NetSuite lookup.
   */
  async getAdjustmentReasons(): Promise<AdjustmentReasonDto[]> {
    const { data } = await apiClient.get<ApiResponse<AdjustmentReasonDto[]>>(
      '/netsuite-lookup/adjustment-reasons'
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to fetch adjustment reasons');
  }
}

export const picklistService = new PicklistService();
